import engineApiSlice from 'src/shared/state/api-slices/engineApiSlice';

/**
 * The engine's versioned dataset catalogue (ADR-016). STAC's nesting is
 * `Collection` → `Item` → `Asset`, which reads here as dataset → version → file.
 *
 * Every type carries an index signature. STAC documents are open — producers
 * attach extension fields (`sci:doi`, `proj:code`) the engine stores verbatim —
 * so a closed type would silently drop them when we read a document and PUT it
 * back. The named fields are the ones the engine's own rules speak about.
 */
export type StacExtent = {
    spatial: { bbox: number[][] };
    /** An open-ended interval ends `null`, which is why these are nullable. */
    temporal: { interval: (string | null)[][] };
};

/** Who had a hand in the data. `roles` is STAC's closed vocabulary for what that hand did. */
export type StacProvider = {
    name: string;
    description?: string;
    roles?: string[];
    url?: string;
};

export type StacCollection = {
    id: string;
    type: 'Collection';
    stac_version: string;
    title?: string;
    description: string;
    license: string;
    keywords?: string[];
    providers?: StacProvider[];
    extent: StacExtent;
    links: unknown[];
    [extensionField: string]: unknown;
};

export type ListStacCollectionsResponse = {
    collections: StacCollection[];
};

/**
 * One file of one version. STAC calls asset keys "non-descriptive", so the key
 * carries no meaning and none is reserved; `roles` is what says what a file is for.
 *
 * The engine requires every asset to have an `s3://` href and a non-blank `type`,
 * and a tile source to declare `proj:code` as well (`stac_item_rules.rs`).
 */
export type StacAsset = {
    href: string;
    type?: string;
    title?: string;
    description?: string;
    roles?: string[];
    [extensionField: string]: unknown;
};

/** One version of a dataset — a single computation of it (ADR-016). */
export type StacItem = {
    id: string;
    type: 'Feature';
    stac_version: string;
    collection?: string;
    geometry: unknown;
    bbox?: number[];
    properties: {
        /** When the producer computed the data. The axis versions resolve on. */
        datetime: string | null;
        /** Both instants are the engine's; anything we send for them is discarded. */
        created?: string;
        updated?: string;
        [extensionField: string]: unknown;
    };
    assets: Record<string, StacAsset>;
    links: unknown[];
};

/**
 * A FeatureCollection holding *at most one* version: the engine answers "which
 * version was in force at this instant", not "which versions exist". An instant
 * before the dataset began is an empty `features`, not a 404 (ADR-016 decision 2).
 */
export type StacItemCollectionResponse = {
    type: 'FeatureCollection';
    features: StacItem[];
    /** OGC's counts. They disagree when the engine capped the listing — the only signal,
     *  since the catalogue puts no paging `links` on a response (ADR-016 decision 11). */
    numberReturned?: number;
    numberMatched?: number;
};

export type ListStacItemsRequest = {
    collectionId: string;
    /** RFC 3339. Omit either end to leave the window open there; omit both for every version. */
    from?: string;
    to?: string;
    limit?: number;
};

export type StacItemRequest = {
    collectionId: string;
    itemId: string;
};

export type StacItemTileLayersResponse = {
    /** The layers the archive declares, in the order it declares them. */
    layers: string[];
};

export type StacItemWriteRequest = {
    collectionId: string;
    item: StacItem;
};

export type StacItemValidAtRequest = {
    collectionId: string;
    /** RFC 3339. The engine rejects the request outright without it. */
    validAt: string;
};

const STAC_COLLECTIONS_LIST_TAG = { type: 'stacCollections', id: 'LIST' } as const;

/** Ids come from the catalogue, but they reach the URL as a path segment, so they are escaped. */
const collectionPath = (id: string) => `/catalogue/collections/${encodeURIComponent(id)}`;
const itemPath = (collectionId: string, itemId: string) => `${collectionPath(collectionId)}/items/${encodeURIComponent(itemId)}`;

const stacCatalogueEngineSlice = engineApiSlice.injectEndpoints({
    endpoints: builder => ({
        listStacCollections: builder.query<ListStacCollectionsResponse, void>({
            query: () => ({
                url: '/catalogue/collections',
                method: 'GET',
            }),
            providesTags: [STAC_COLLECTIONS_LIST_TAG],
        }),
        getStacCollection: builder.query<StacCollection, string>({
            query: id => ({
                url: collectionPath(id),
                method: 'GET',
            }),
            providesTags: (_result, _error, id) => [{ type: 'stacCollections', id }],
        }),
        getStacItemValidAt: builder.query<StacItemCollectionResponse, StacItemValidAtRequest>({
            query: ({ collectionId, validAt }) => ({
                url: `${collectionPath(collectionId)}/items`,
                method: 'GET',
                params: { valid_at: validAt },
            }),
            providesTags: (_result, _error, { collectionId }) => [{ type: 'stacItems', id: collectionId }],
        }),
        createStacCollection: builder.mutation<StacCollection, StacCollection>({
            query: collection => ({
                url: '/catalogue/collections',
                method: 'POST',
                body: collection,
            }),
            invalidatesTags: [STAC_COLLECTIONS_LIST_TAG],
        }),
        updateStacCollection: builder.mutation<StacCollection, StacCollection>({
            query: collection => ({
                url: collectionPath(collection.id),
                method: 'PUT',
                body: collection,
            }),
            invalidatesTags: (_result, _error, collection) => [STAC_COLLECTIONS_LIST_TAG, { type: 'stacCollections', id: collection.id }],
        }),
        listStacItems: builder.query<StacItemCollectionResponse, ListStacItemsRequest>({
            query: ({ collectionId, from, to, limit }) => ({
                url: `${collectionPath(collectionId)}/items`,
                method: 'GET',
                params: {
                    // OGC's interval syntax, which STAC inherits: `..` leaves an end open.
                    // Neither end given is every version, which is what omitting it means.
                    ...(from || to ? { datetime: `${from ?? '..'}/${to ?? '..'}` } : {}),
                    ...(limit ? { limit } : {}),
                },
            }),
            providesTags: (_result, _error, { collectionId }) => [{ type: 'stacItems', id: collectionId }],
        }),
        getStacItemTileLayers: builder.query<StacItemTileLayersResponse, StacItemRequest>({
            query: ({ collectionId, itemId }) => ({
                url: `${itemPath(collectionId, itemId)}/tiles/layers`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { collectionId }) => [{ type: 'stacItems', id: collectionId }],
        }),
        getStacItem: builder.query<StacItem, StacItemRequest>({
            query: ({ collectionId, itemId }) => ({
                url: itemPath(collectionId, itemId),
                method: 'GET',
            }),
            providesTags: (_result, _error, { collectionId }) => [{ type: 'stacItems', id: collectionId }],
        }),
        createStacItem: builder.mutation<StacItem, StacItemWriteRequest>({
            query: ({ collectionId, item }) => ({
                url: `${collectionPath(collectionId)}/items`,
                method: 'POST',
                body: item,
            }),
            invalidatesTags: (_result, _error, { collectionId }) => [{ type: 'stacItems', id: collectionId }],
        }),
        updateStacItem: builder.mutation<StacItem, StacItemWriteRequest>({
            query: ({ collectionId, item }) => ({
                url: itemPath(collectionId, item.id),
                method: 'PUT',
                body: item,
            }),
            invalidatesTags: (_result, _error, { collectionId }) => [{ type: 'stacItems', id: collectionId }],
        }),
    }),
});

export const {
    useListStacCollectionsQuery,
    useGetStacCollectionQuery,
    useGetStacItemValidAtQuery,
    useListStacItemsQuery,
    useCreateStacCollectionMutation,
    useUpdateStacCollectionMutation,
    useGetStacItemQuery,
    useGetStacItemTileLayersQuery,
    useCreateStacItemMutation,
    useUpdateStacItemMutation,
} = stacCatalogueEngineSlice;

export default stacCatalogueEngineSlice;
